/**
 * WizardStep Components
 *
 * Individual step components for the character creation wizard.
 * Each step handles a specific aspect of character building.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { TRAIT_SUGGESTIONS, AVATAR_EMOJIS } from '@/services/characterLibraryService'
import type { CharacterWizardData } from '@/services/characterLibraryService'

// =============================================================================
// Shared Types
// =============================================================================

interface StepProps {
  data: CharacterWizardData
  onChange: (updates: Partial<CharacterWizardData>) => void
}

// =============================================================================
// Step 1: Name & Avatar
// =============================================================================

export function NameAvatarStep({ data, onChange }: StepProps) {
  const [showAllEmojis, setShowAllEmojis] = useState(false)
  const displayEmojis = showAllEmojis ? AVATAR_EMOJIS : AVATAR_EMOJIS.slice(0, 16)

  return (
    <div className="space-y-6">
      {/* Name input */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-2">
          What's your character's name?
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Enter a name..."
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-primary)]/20',
            'text-[var(--color-kids-text)] text-lg font-display',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-primary)] focus:ring-2 focus:ring-[var(--color-kids-primary)]/20',
            'transition-all'
          )}
          autoFocus
        />
      </div>

      {/* Avatar picker */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-2">
          Pick an avatar
        </label>
        <div className="grid grid-cols-8 gap-2">
          {displayEmojis.map((emoji) => (
            <motion.button
              key={emoji}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onChange({ avatar_emoji: emoji })}
              className={cn(
                'w-10 h-10 rounded-lg text-2xl flex items-center justify-center transition-all',
                data.avatar_emoji === emoji
                  ? 'bg-[var(--color-kids-primary)] shadow-lg'
                  : 'bg-[var(--color-kids-surface-alt)] hover:bg-[var(--color-kids-primary)]/20'
              )}
            >
              {emoji}
            </motion.button>
          ))}
        </div>
        {!showAllEmojis && (
          <button
            onClick={() => setShowAllEmojis(true)}
            className="mt-2 text-sm text-[var(--color-kids-primary)] hover:underline"
          >
            Show more avatars
          </button>
        )}
      </div>

      {/* Preview */}
      <Card className="bg-[var(--color-kids-primary)]/5 border-[var(--color-kids-primary)]/20">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center text-4xl">
            {data.avatar_emoji || '👤'}
          </div>
          <div>
            <p className="text-sm text-[var(--color-kids-text-muted)]">Preview</p>
            <p className="font-display font-bold text-xl text-[var(--color-kids-text)]">
              {data.name || 'Your Character'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Step 2: Traits
// =============================================================================

export function TraitsStep({ data, onChange }: StepProps) {
  const [customTrait, setCustomTrait] = useState('')
  const traits = data.traits || []
  const maxTraits = 5

  const addTrait = (trait: string) => {
    if (traits.length < maxTraits && !traits.includes(trait)) {
      onChange({ traits: [...traits, trait] })
    }
  }

  const removeTrait = (trait: string) => {
    onChange({ traits: traits.filter(t => t !== trait) })
  }

  const handleAddCustom = () => {
    const trimmed = customTrait.trim().toLowerCase()
    if (trimmed && traits.length < maxTraits && !traits.includes(trimmed)) {
      addTrait(trimmed)
      setCustomTrait('')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          What is {data.name || 'your character'} like?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-4">
          Pick up to {maxTraits} personality traits
        </p>

        {/* Selected traits */}
        {traits.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {traits.map((trait) => (
              <motion.span
                key={trait}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--color-kids-primary)] text-white text-sm font-medium"
              >
                {trait}
                <button
                  onClick={() => removeTrait(trait)}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </motion.span>
            ))}
          </div>
        )}

        {/* Trait suggestions */}
        <div className="flex flex-wrap gap-2 mb-4">
          {TRAIT_SUGGESTIONS.filter(t => !traits.includes(t)).map((trait) => (
            <button
              key={trait}
              onClick={() => addTrait(trait)}
              disabled={traits.length >= maxTraits}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                traits.length >= maxTraits
                  ? 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-light)] cursor-not-allowed'
                  : 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)] hover:bg-[var(--color-kids-primary)]/20 hover:text-[var(--color-kids-primary)]'
              )}
            >
              {trait}
            </button>
          ))}
        </div>

        {/* Custom trait input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={customTrait}
            onChange={(e) => setCustomTrait(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()}
            placeholder="Add your own trait..."
            disabled={traits.length >= maxTraits}
            className={cn(
              'flex-1 px-4 py-2 rounded-[var(--radius-kid)]',
              'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-surface-alt)]',
              'text-[var(--color-kids-text)]',
              'placeholder:text-[var(--color-kids-text-light)]',
              'focus:outline-none focus:border-[var(--color-kids-primary)]',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          />
          <Button
            variant="secondary"
            onClick={handleAddCustom}
            disabled={!customTrait.trim() || traits.length >= maxTraits}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <p className="text-xs text-[var(--color-kids-text-muted)] mt-2">
          {traits.length} / {maxTraits} traits selected
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// Step 3: Wants & Fears
// =============================================================================

export function WantsFearStep({ data, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      {/* Wants */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          What does {data.name || 'your character'} want most?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          This is their main goal or dream
        </p>
        <textarea
          value={data.wants || ''}
          onChange={(e) => onChange({ wants: e.target.value })}
          placeholder="Example: to become the best artist in school"
          rows={2}
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-success)]/20',
            'text-[var(--color-kids-text)]',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-success)] focus:ring-2 focus:ring-[var(--color-kids-success)]/20',
            'resize-none'
          )}
        />
      </div>

      {/* Fears */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          What is {data.name || 'your character'}'s biggest fear?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          This creates obstacles in stories
        </p>
        <textarea
          value={data.fear || ''}
          onChange={(e) => onChange({ fear: e.target.value })}
          placeholder="Example: being laughed at for their drawings"
          rows={2}
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-warning)]/20',
            'text-[var(--color-kids-text)]',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-warning)] focus:ring-2 focus:ring-[var(--color-kids-warning)]/20',
            'resize-none'
          )}
        />
      </div>

      {/* Tip */}
      <Card className="bg-[var(--color-kids-surface-alt)]">
        <CardContent className="p-4">
          <p className="text-sm text-[var(--color-kids-text)]">
            <span className="font-semibold">Tip:</span> The best stories happen when a character's fear gets in the way of what they want!
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Step 4: Secret & Item
// =============================================================================

export function SecretItemStep({ data, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      {/* Secret */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          Does {data.name || 'your character'} have a secret?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          Something hidden that could surprise others (optional)
        </p>
        <textarea
          value={data.secret || ''}
          onChange={(e) => onChange({ secret: e.target.value })}
          placeholder="Example: they can talk to animals but no one believes them"
          rows={2}
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-accent)]/20',
            'text-[var(--color-kids-text)]',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-accent)] focus:ring-2 focus:ring-[var(--color-kids-accent)]/20',
            'resize-none'
          )}
        />
      </div>

      {/* Signature Item */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          What special item does {data.name || 'your character'} always carry?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          A signature possession that's important to them (optional)
        </p>
        <input
          type="text"
          value={data.signature_item || ''}
          onChange={(e) => onChange({ signature_item: e.target.value })}
          placeholder="Example: a lucky red scarf from their grandmother"
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-secondary)]/20',
            'text-[var(--color-kids-text)]',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-secondary)] focus:ring-2 focus:ring-[var(--color-kids-secondary)]/20'
          )}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Step 5: Voice Sample
// =============================================================================

export function VoiceStep({ data, onChange }: StepProps) {
  return (
    <div className="space-y-6">
      {/* Voice sample */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          How does {data.name || 'your character'} talk?
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          Write a line of dialogue that sounds like them
        </p>
        <textarea
          value={data.voice_sample || ''}
          onChange={(e) => onChange({ voice_sample: e.target.value })}
          placeholder={`Example: "Come on, let's go! Adventure awaits!"`}
          rows={3}
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-primary)]/20',
            'text-[var(--color-kids-text)] italic',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-primary)] focus:ring-2 focus:ring-[var(--color-kids-primary)]/20',
            'resize-none'
          )}
        />
      </div>

      {/* Backstory (optional) */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-kids-text)] mb-1">
          Any backstory? (optional)
        </label>
        <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
          Where are they from? What happened to them before your story begins?
        </p>
        <textarea
          value={data.backstory || ''}
          onChange={(e) => onChange({ backstory: e.target.value })}
          placeholder="Example: They grew up on a farm and always dreamed of seeing the ocean..."
          rows={3}
          className={cn(
            'w-full px-4 py-3 rounded-[var(--radius-kid)]',
            'bg-[var(--color-kids-surface)] border-2 border-[var(--color-kids-surface-alt)]',
            'text-[var(--color-kids-text)]',
            'placeholder:text-[var(--color-kids-text-light)]',
            'focus:outline-none focus:border-[var(--color-kids-primary)]',
            'resize-none'
          )}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Progress Indicator
// =============================================================================

interface ProgressIndicatorProps {
  currentStep: number
  totalSteps: number
  labels: string[]
}

export function ProgressIndicator({ currentStep, totalSteps }: ProgressIndicatorProps) {
  return (
    <div className="flex items-center justify-between mb-6">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div key={index} className="flex items-center">
          {/* Step circle */}
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all',
              index < currentStep
                ? 'bg-[var(--color-kids-success)] text-white'
                : index === currentStep
                  ? 'bg-[var(--color-kids-primary)] text-white'
                  : 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)]'
            )}
          >
            {index < currentStep ? (
              <Check className="w-4 h-4" />
            ) : (
              index + 1
            )}
          </div>

          {/* Connector line */}
          {index < totalSteps - 1 && (
            <div
              className={cn(
                'w-full h-1 mx-2 rounded transition-all',
                index < currentStep
                  ? 'bg-[var(--color-kids-success)]'
                  : 'bg-[var(--color-kids-surface-alt)]'
              )}
              style={{ minWidth: '40px' }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default {
  NameAvatarStep,
  TraitsStep,
  WantsFearStep,
  SecretItemStep,
  VoiceStep,
  ProgressIndicator,
}
